import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import { get_automation_static_file_datasets_url, get_process_dataset_url } from '@/utils/urls.ts';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import useAutomationId from '@/hooks/useAutomationId.ts';
import { STATIC_DATASETS_KEY, STATIC_DATASET_USAGE_KEY } from '@/utils/queryKeys.ts';
import {
  ManualDatasetCreateResponse,
  ManualNonDirectoryDatasetCreateRequest,
} from '@/hooks/api/datasets/static/types.ts';
import {
  getSignedUploadConfig,
  normalizeUploadContentType,
} from '@/hooks/api/datasets/uploadMetadata.ts';

type props = {
  name: string;
  file: File;
};

const useCreateStaticDataset = (
  options: UseMutationOptions<ManualDatasetCreateResponse, AxiosError, props>,
): UseMutationResult<ManualDatasetCreateResponse, AxiosError, props> => {
  const apiClient = useApiClient();
  const automationId = useAutomationId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ name, file }: props) => {
      const postData: ManualNonDirectoryDatasetCreateRequest = {
        name: name,
        path: file.name,
        content_type: file.type ? normalizeUploadContentType(file.type) : null,
      };

      const response = await apiClient.post<ManualDatasetCreateResponse>(
        get_automation_static_file_datasets_url(automationId),
        postData,
      );

      const datasetId = response.data.dataset.id;
      const presignedUrl = Object.values(response.data.urls)[0];
      if (!presignedUrl) {
        throw new Error(`Missing upload URL for ${file.name}`);
      }
      const contentType = file.type ? normalizeUploadContentType(file.type) : null;
      await axios.put(presignedUrl, file, getSignedUploadConfig(contentType));

      await apiClient.put(get_process_dataset_url(datasetId), {});

      void queryClient.invalidateQueries({ queryKey: STATIC_DATASET_USAGE_KEY(automationId) });
      await queryClient.invalidateQueries({ queryKey: STATIC_DATASETS_KEY(automationId) });

      return response.data;
    },
    ...options,
  });
};

export default useCreateStaticDataset;
